import React from 'react';
import type { CurationCandidate } from '../api/types';
import { STUDIO_COPY } from '../config';
import {
  formatClipSec,
  type ClipBounds,
} from './customizeHelpers';

type CustomizePickStepProps = {
  candidates: CurationCandidate[];
  orderedIds: string[];
  bounds: Record<string, ClipBounds>;
  maxClips: number;
  maxDurationSec: number;
  totalDuration: number;
  overClipCap: boolean;
  overDurationCap: boolean;
  disabled?: boolean;
  previewAvailable?: boolean;
  activePreviewId?: string | null;
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onPreview: (candidate: CurationCandidate) => void;
  onNext: () => void;
};

const CustomizePickStep: React.FC<CustomizePickStepProps> = ({
  candidates,
  orderedIds,
  bounds,
  maxClips,
  maxDurationSec,
  totalDuration,
  overClipCap,
  overDurationCap,
  disabled = false,
  previewAvailable = false,
  activePreviewId = null,
  onToggle,
  onRemove,
  onPreview,
  onNext,
}) => {
  const selected = new Set(orderedIds);
  const byId = new Map(candidates.map((c) => [c.candidate_id, c]));

  return (
    <div className="studio-customize-pick" data-testid="studio-customize-pick">
      <p
        className="studio-customize-helper"
        data-testid="studio-customize-step1-hint"
      >
        {STUDIO_COPY.customizeStep1Hint}
      </p>
      <p
        className="studio-customize-helper"
        data-testid="studio-customize-stats"
      >
        {STUDIO_COPY.customizeStatsTemplate
          .replace('{count}', String(orderedIds.length))
          .replace('{max}', String(maxClips))
          .replace('{duration}', String(Math.round(totalDuration)))
          .replace('{maxDuration}', String(Math.round(maxDurationSec)))}
      </p>

      <ul className="studio-customize-pick-grid" data-testid="studio-customize-pick-grid">
        {candidates.map((c) => {
          const isOn = selected.has(c.candidate_id);
          const isPreviewing = activePreviewId === c.candidate_id;
          const b = bounds[c.candidate_id];
          const dur = b ? Math.max(0, b.end - b.start) : c.duration_sec;
          return (
            <li key={c.candidate_id}>
              <div
                className={`studio-customize-pick-tile ${
                  isOn ? 'is-selected' : ''
                } ${isPreviewing ? 'is-previewing' : ''}`}
                data-testid={`studio-customize-pick-${c.candidate_id}`}
              >
                <button
                  type="button"
                  className="studio-customize-pick-thumb"
                  disabled={disabled || !previewAvailable}
                  title={
                    previewAvailable
                      ? STUDIO_COPY.customizePreviewTooltip
                      : 'Preview unavailable — refresh the source link above'
                  }
                  aria-label={`Preview ${c.label}`}
                  data-testid={`studio-customize-preview-${c.candidate_id}`}
                  onClick={() => onPreview(c)}
                >
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url} alt="" />
                  ) : (
                    <span className="studio-customize-pick-thumb-fallback">
                      {formatClipSec(dur)}
                    </span>
                  )}
                  <span className="studio-customize-pick-play" aria-hidden>
                    {STUDIO_COPY.customizePickPreview}
                  </span>
                </button>
                <div className="studio-customize-pick-meta">
                  <p className="font-medium text-sm truncate">{c.label}</p>
                  <p className="text-xs text-[var(--studio-muted)] truncate">
                    {c.team ? `${c.team} · ` : ''}
                    {formatClipSec(dur)}
                  </p>
                  {c.why ? (
                    <p className="text-xs text-[var(--studio-muted)] line-clamp-2">
                      {c.why}
                    </p>
                  ) : null}
                  <button
                    type="button"
                    className={`studio-customize-pick-select ${
                      isOn ? 'is-on' : ''
                    }`}
                    aria-pressed={isOn}
                    disabled={disabled || (!isOn && orderedIds.length >= maxClips)}
                    data-testid={`studio-customize-select-${c.candidate_id}`}
                    onClick={() => onToggle(c.candidate_id)}
                  >
                    {isOn
                      ? STUDIO_COPY.customizePickSelected
                      : STUDIO_COPY.customizePickSelect}
                    {isOn ? (
                      <span className="studio-customize-pick-check" aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {overClipCap ? (
        <p className="text-sm text-center text-[var(--studio-danger)]" role="alert">
          {STUDIO_COPY.customizeClipCap.replace('{max}', String(maxClips))}
        </p>
      ) : null}
      {overDurationCap ? (
        <p className="text-sm text-center text-[var(--studio-danger)]" role="alert">
          {STUDIO_COPY.customizeDurationCap.replace(
            '{max}',
            String(Math.round(maxDurationSec))
          )}
        </p>
      ) : null}

      <div
        className="studio-customize-tray"
        data-testid="studio-customize-tray"
      >
        <p className="text-xs text-[var(--studio-muted)] mb-2">
          {STUDIO_COPY.customizeSelectedTray.replace(
            '{count}',
            String(orderedIds.length)
          )}
        </p>
        <ol className="studio-customize-tray-strip">
          {orderedIds.map((id, index) => {
            const c = byId.get(id);
            if (!c) return null;
            return (
              <li
                key={id}
                className="studio-customize-tray-chip"
                data-testid={`studio-customize-tray-${id}`}
              >
                <span className="studio-customize-tray-num">{index + 1}</span>
                <span className="truncate">{c.label}</span>
                <button
                  type="button"
                  className="studio-customize-tray-remove"
                  aria-label={`Remove ${c.label}`}
                  disabled={disabled || orderedIds.length <= 1}
                  onClick={() => onRemove(id)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ol>
        <button
          type="button"
          className="studio-btn-primary w-full mt-3 studio-customize-action-primary"
          data-testid="studio-customize-next"
          disabled={
            disabled ||
            orderedIds.length < 1 ||
            overClipCap ||
            overDurationCap
          }
          onClick={onNext}
        >
          {STUDIO_COPY.customizeNextArrange}
        </button>
      </div>
    </div>
  );
};

export default CustomizePickStep;
